import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import { ApiCallError, callFunction } from '@/lib/api';
import { AuthShell } from './AuthShell';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { ErrorAlert } from '@/components/ui/ErrorAlert';

type ClinicType = 'psychologist' | 'dentist' | 'specialist' | 'emdr_therapist';

interface CreateClinicOutput {
  user_id: string;
  email: string;
  clinic_id: string;
  clinic_slug: string;
  trial_ends_at: string;
  session: {
    access_token: string;
    refresh_token: string;
  };
}

const CLINIC_TYPES: ClinicType[] = ['psychologist', 'dentist', 'specialist', 'emdr_therapist'];

export function CreateClinic() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [adminName, setAdminName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [clinicName, setClinicName] = useState('');
  const [clinicType, setClinicType] = useState<ClinicType>('psychologist');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);

    if (password.length < 8) {
      setError(t('auth.reset_password.min_length'));
      return;
    }
    if (password !== confirm) {
      setError(t('auth.reset_password.mismatch'));
      return;
    }
    if (clinicName.trim().length < 2) {
      setError(t('auth.create_clinic.clinic_name_required'));
      return;
    }

    setSubmitting(true);
    try {
      const result = await callFunction<
        {
          email: string;
          password: string;
          clinic_name: string;
          clinic_type: ClinicType;
          admin_name?: string;
        },
        CreateClinicOutput
      >('create-clinic', {
        email: email.trim(),
        password,
        clinic_name: clinicName.trim(),
        clinic_type: clinicType,
        admin_name: adminName.trim() || undefined,
      });

      const { error: setErr } = await supabase.auth.setSession({
        access_token: result.session.access_token,
        refresh_token: result.session.refresh_token,
      });
      if (setErr) throw setErr;

      try {
        localStorage.setItem('clinio_active_clinic_id', result.clinic_id);
      } catch {
        // localStorage not available
      }

      navigate('/dashboard', { replace: true });
    } catch (err) {
      if (err instanceof ApiCallError) {
        switch (err.code) {
          case 'conflict':
            setError(t('auth.create_clinic.error_conflict'));
            break;
          case 'invalid-input':
            setError(t('auth.create_clinic.error_invalid_input'));
            break;
          default:
            setError(err.message || t('auth.errors.unknown'));
        }
      } else {
        setError(err instanceof Error ? err.message : t('auth.errors.unknown'));
      }
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      title={t('auth.create_clinic.title')}
      subtitle={t('auth.create_clinic.subtitle')}
      footer={
        <>
          {t('auth.create_clinic.have_account')}{' '}
          <Link to="/sign-in" className="font-medium text-indigo-600 hover:text-indigo-500">
            {t('auth.create_clinic.sign_in_link')}
          </Link>
        </>
      }
    >
      <div className="mb-6 rounded-md border border-indigo-100 bg-indigo-50 p-4 text-sm text-indigo-900 dark:border-indigo-800/40 dark:bg-indigo-950/40 dark:text-indigo-200">
        <p className="font-semibold">{t('auth.create_clinic.trial_heading')}</p>
        <p className="mt-1 text-indigo-800 dark:text-indigo-300">
          {t('auth.create_clinic.trial_body')}
        </p>
      </div>

      {error && <ErrorAlert message={error} />}

      <form onSubmit={handleSubmit} className="space-y-5">
        <Input
          label={t('auth.create_clinic.clinic_name')}
          name="clinicName"
          type="text"
          autoComplete="organization"
          required
          value={clinicName}
          onChange={(e) => setClinicName(e.target.value)}
        />

        <div>
          <label
            htmlFor="clinicType"
            className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            {t('auth.create_clinic.clinic_type')}
          </label>
          <select
            id="clinicType"
            name="clinicType"
            required
            value={clinicType}
            onChange={(e) => setClinicType(e.target.value as ClinicType)}
            className="block h-[44px] w-full rounded-xl border border-gray-300 bg-white px-3 text-gray-900 focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          >
            {CLINIC_TYPES.map((type) => (
              <option key={type} value={type}>
                {t(`settings.general.clinic_type_${type}`)}
              </option>
            ))}
          </select>
        </div>

        <Input
          label={t('auth.create_clinic.admin_name')}
          name="adminName"
          type="text"
          autoComplete="name"
          value={adminName}
          onChange={(e) => setAdminName(e.target.value)}
          hint={t('auth.create_clinic.admin_name_hint')}
        />

        <Input
          label={t('auth.create_clinic.email')}
          name="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        <Input
          label={t('auth.create_clinic.password')}
          name="password"
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          hint={t('auth.accept_invitation.password_hint')}
        />

        <Input
          label={t('auth.create_clinic.password_confirm')}
          name="passwordConfirm"
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />

        <Button type="submit" size="full" disabled={submitting}>
          {submitting ? t('auth.create_clinic.submitting') : t('auth.create_clinic.submit')}
        </Button>
      </form>
    </AuthShell>
  );
}
